import type {
  EnhancedConfigTypeId,
  EnhancedLanguageId,
  EnhancedManCodeId,
  EnhancedMarketId,
  EnhancedVehicleTypeId,
  EnhancedViewId,
} from '#api/enhancements/enhancedTmsApiTypes.ts'
import type { ConfigurationStep } from '#features/Configuration/ConfigurationStep'
import type { DateString } from '#utils/DateString'

export type ConfigurationMode = 'writable' | 'read-only' | 'stashed' | 'cloned'

export interface FullApiConfiguration {
  step?: ConfigurationStep
  backUrl?: string

  // YYYY-MM-DD
  partPeriod?: DateString
  // YYYY-MM-DD
  desiredDealerDate?: DateString
  desiredDealerDateIsEstimate?: boolean
  // YYYY-MM-DD
  pricingDate?: DateString

  market: EnhancedMarketId
  vehicleType: EnhancedVehicleTypeId
  configurationType?: EnhancedConfigTypeId
  view: EnhancedViewId
  language?: EnhancedLanguageId

  callingSystem?: string

  manCode?: EnhancedManCodeId
  mode?: ConfigurationMode
  source?: 'reference-truck' | 'user-input' | 'restart' | 'code-info-overlay' | undefined

  orderCountry?: string
  orderLanguage?: string

  // allows to override the marketId in the market API call that loads the national codes
  nationalCodeMarketIdOverride?: EnhancedMarketId
  // it's possible to pass a desired title with the configuration, ie when creating a reference truck
  title?: string
}

export type ManCodeApiConfiguration = Partial<FullApiConfiguration> & {
  manCode: EnhancedManCodeId
  mode: ConfigurationMode
  // different sources have different behavior, reference-truck strips their history before creating
  source: 'reference-truck' | 'user-input' | 'restart' | 'code-info-overlay' | undefined
}

export type Tg3ApiConfiguration = {
  tg3Code: string
  orderLanguage?: string
  orderCountry?: string
}

export type OverrideableApiConfiguration = FullApiConfiguration | ManCodeApiConfiguration

export type ApiConfiguration = ManCodeApiConfiguration | FullApiConfiguration | Tg3ApiConfiguration

export function isManCodeApiConfiguration(config: ApiConfiguration | undefined): config is ManCodeApiConfiguration {
  return !!config && 'manCode' in config && config.manCode !== undefined
}

export function isFullApiConfiguration(config: ApiConfiguration | undefined): config is FullApiConfiguration {
  return !!config && 'market' in config && 'vehicleType' in config && 'view' in config && config.manCode === undefined
}

export function isTg3ApiConfiguration(config: ApiConfiguration | undefined): config is Tg3ApiConfiguration {
  return !!config && 'tg3Code' in config && config.tg3Code !== undefined
}
